import { ClientNotificationService, EClientNotificationsEventTypes, INotification } from '@shared/push-notification';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { effect, inject, untracked } from '@angular/core';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, switchMap, tap } from 'rxjs';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { tapResponse } from '@ngrx/operators';

export interface IClientNotificationStore {
  unreadNotificationsCount: number;
  notifications: {
    eventData: any;
    eventName: string;
  };
  signalREvent: any;
  notificationsMenu: {
    opened: boolean;
    notifications: INotification[];
    currentPage: number;
    hasMoreItems: boolean;
    origin: string | null;
  };
}

export const clientNotificationInitialStore: IClientNotificationStore = {
  unreadNotificationsCount: 0,
  notifications: null,
  signalREvent: null,
  notificationsMenu: {
    opened: false,
    notifications: [],
    currentPage: 1,
    hasMoreItems: true,
    origin: null,
  },
};

export const ClientNotificationStore = signalStore(
  { providedIn: 'root' },
  withState(clientNotificationInitialStore),
  withMethods(
    (
      state,
      clientNotificationService = inject(ClientNotificationService)
    ) => {

      const normalizeNotifications = (notifications) => {
        notifications.forEach(notification => {
          const [message, ...url] = notification.Message.split('.');
          notification.Message = message;
          const reatachUrl = url.join('.')
          notification.Url = reatachUrl.replace(/.*href=(['"])(.*?)\1.*/i, '$2');
        })
        notifications.sort((a, b) => b.OccurredOn.localeCompare(a.OccurredOn));
        return notifications;
      }

      /**
       * Tipos de evento da ligação aberta, guardados para a poder reabrir. O hub
       * leva a identidade na PRÓPRIA URL
       * (`/NotificationHub?TenantId=...&UserId=...`), fixada no momento em que
       * se liga — mudar de conta sem recarregar a página (o
       * `animateAccountSwitch` faz exatamente isso) deixava o socket preso à
       * pessoa anterior, a empurrar as notificações dela para a lista da nova.
       */
      let listeningEvents: EClientNotificationsEventTypes[] | null = null;

      const listenConnection = rxMethod<EClientNotificationsEventTypes[]>(
          pipe(
            switchMap((eventTypes: EClientNotificationsEventTypes[]) =>
              clientNotificationService.openConnection(eventTypes).pipe(
                tapResponse({
                  next: (notifications) => {
                    let menuNotifications = state.notificationsMenu().notifications;
                    if (
                      notifications.eventName === EClientNotificationsEventTypes.onCentralNotificationContract ||
                      notifications.eventName === EClientNotificationsEventTypes.onGympassCheckinNotification ||
                      notifications.eventName === EClientNotificationsEventTypes.onTotalpassCheckinNotification) {
                      notifications.eventData.ReadedOn = false;
                      notifications.eventData.isNew = true;
                      menuNotifications = normalizeNotifications([notifications.eventData, ...menuNotifications]);
                    }

                    patchState(state, {
                      notifications,
                      notificationsMenu: { ...state.notificationsMenu(), notifications: menuNotifications },
                      ...setPropLoaded('listen'),
                    })
                  },
                  error: (error) =>
                    patchState(state, { ...setPropError('listen', error) }),
                })
              )
            )
          )
        );

      const listen = (eventTypes: EClientNotificationsEventTypes[]) => {
        listeningEvents = eventTypes;
        listenConnection(eventTypes);
      };

      /**
       * Reabre a ligação com a identidade em vigor. Chamado pelo hook da sessão.
       * O `switchMap` fecha a anterior antes de abrir a nova; e sem sessão o
       * `openConnection` completa sem ligar, portanto isto também serve para
       * fechar o socket de quem saiu.
       */
      const reopenListenConnection = () => {
        if (!listeningEvents) return;
        listenConnection(listeningEvents);
      };

      const readAllNotifications = rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('readNotifications') })),
          switchMap(() => clientNotificationService.setReadedAllNotifications()
            .pipe(
              tapResponse({
                next: () => {
                  const notifications = state.notificationsMenu().notifications;
                  notifications.forEach(notification => {
                    if (!notification?.ReadedOn) {
                      notification.ReadedOn = true;
                    }
                  });

                  patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications }, ...setPropLoaded('readNotifications') })
                },
                error: (error) => patchState(state, { ...setPropError('readNotifications', error) })
              })
            )
          )
        )
      )
      const loadNotifications = rxMethod<void>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('loadNotifications') })),
          switchMap(() => clientNotificationService.getNotifications(1, 14, true, state.notificationsMenu().origin ?? undefined)
            .pipe(
              tapResponse({
                next: (data) => {
                  // Um pedido em voo quando a sessão cai chega DEPOIS de o hook
                  // ter limpado a lista, e repunha as notificações de quem
                  // saiu. No nativo isso é visível: o `logout()` só navega para
                  // `/onboarding`, a página não recarrega e este store sobrevive.
                  if (!clientNotificationService.sessionKey()) return;

                  const notifications = normalizeNotifications(data);
                  patchState(state, {
                    notificationsMenu: {
                      ...state.notificationsMenu(),
                      notifications,
                      currentPage: 1,
                      hasMoreItems: data.length === 14
                    },
                    ...setPropLoaded('loadNotifications')
                  })
                },
                error: (error) => patchState(state, { ...setPropError('loadNotifications', error) })
              })
            )
          )
        )
      )

      return {
        readAllNotifications,
        loadNotifications,
        filterByOrigin: (origin: string | null) => {
          patchState(state, {
            notificationsMenu: {
              ...state.notificationsMenu(),
              origin,
              notifications: [],
              currentPage: 1,
              hasMoreItems: true,
            },
          });
          loadNotifications();
        },
        toggleNotificationsMenu: (open?: boolean) => {
          const unReadedNotifications = state.notificationsMenu().notifications.filter((notification) => !notification.ReadedOn);

          if (state.notificationsMenu().opened && unReadedNotifications?.length) {
            readAllNotifications();
          }
          patchState(state, { notificationsMenu: { ...state.notificationsMenu(), opened: open ?? !state.notificationsMenu().opened } });
        },
        removeNotification: (notificationId: string) => {
          const notifications = state.notificationsMenu().notifications.filter((notification) => notification.NotificationId !== notificationId);
          patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications } });
        },
        listen,
        reopenListenConnection,
        loadMoreNotifications: rxMethod<void>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('loadMoreNotifications') })),
            switchMap(() => {
              const currentNotifications = state.notificationsMenu();
              const nextPage = currentNotifications.currentPage + 1;

              return clientNotificationService.getNotifications(nextPage, 14, true, currentNotifications.origin ?? undefined);
            }),
            tapResponse({
              next: (data: INotification[]) => {
                // Mesmo motivo do `loadNotifications`.
                if (!clientNotificationService.sessionKey()) return;

                const currentItems = state.notificationsMenu().notifications;
                const newNotifications = normalizeNotifications(data);

                patchState(state, {
                  notificationsMenu: {
                    ...state.notificationsMenu(),
                    notifications: currentItems.concat(newNotifications),
                    currentPage: state.notificationsMenu().currentPage + 1,
                    hasMoreItems: data.length === 14
                  },
                  ...setPropLoaded('loadMoreNotifications')
                });
              },
              error: (error) => patchState(state, { ...setPropError('loadMoreNotifications', error) })
            })
          )
        ),
        deleteNotification: rxMethod<string>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('removeNotification') })),
            switchMap((notificationId) =>
              clientNotificationService.deleteNotification(notificationId).pipe(
                tapResponse({
                  next: () => {
                    const notifications = state.notificationsMenu().notifications.filter((notification) => notification.NotificationId !== notificationId);
                    patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications }, ...setPropLoaded('removeNotification') })
                  },
                  error: (error) => patchState(state, { ...setPropError('removeNotification', error) })
                })
              )
            )
          )
        ),
        /**
         * Exclui a notificação sem feedback próprio, para quando a ação que a tornou
         * obsoleta já anunciou o seu sucesso — é o caso da validação de check-in.
         * O req-state é separado do `deleteNotification` de propósito: os itens da
         * lista reagem ao `removeNotificationLoaded` para dizer "notificação
         * excluída", e por aqui isso seria um segundo toast em cima do da validação.
         */
        deleteNotificationSilently: rxMethod<string>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('deleteNotificationSilently') })),
            switchMap((notificationId) =>
              clientNotificationService.deleteNotification(notificationId).pipe(
                tapResponse({
                  next: () => {
                    const notifications = state.notificationsMenu().notifications.filter((notification) => notification.NotificationId !== notificationId);
                    patchState(state, { notificationsMenu: { ...state.notificationsMenu(), notifications }, ...setPropLoaded('deleteNotificationSilently') })
                  },
                  error: (error) => patchState(state, { ...setPropError('deleteNotificationSilently', error) })
                })
              )
            )
          )
        ),
        subscribePushNotifications: rxMethod(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('subscribePushNotifications') })),
            switchMap(() => clientNotificationService.openConnection(['onCentralNotification'])
              .pipe(
                tapResponse({
                  next: (notifications) =>
                    patchState(state, {
                      notificationsMenu: { ...state.notificationsMenu(), notifications },
                      ...setPropLoaded('subscribePushNotifications')
                    }),
                  error: (error) => patchState(state, { ...setPropError('subscribePushNotifications', error) })
                })
              )
            )
          )
        ),

        setPropsBlankState: (props: string[]) => {
          patchState(state, { ...setPropInit(...props) })
        },
      }
    }
  ),
  withReqState('loadNotifications', 'listen', 'readNotifications', 'removeNotification', 'deleteNotificationSilently', 'loadMoreNotifications'),
  withHooks({
    onInit: (state, clientNotificationService = inject(ClientNotificationService)) => {
      // Este store é `providedIn: 'root'` e o hook corre assim que alguém o
      // injeta — o que inclui o instante logo a seguir ao logout, com a SPA
      // ainda viva a caminho do redirect. Sem sessão não há identidade para
      // levar no pedido, e era assim que a query saía com
      // `tenantId=undefined&userId=undefined` e apanhava 400.
      //
      // Mas uma pergunta única no arranque não chegava: a sessão também pode
      // chegar DEPOIS (login sem recarregar a página) e pode MUDAR (o
      // `changePersonAccount` troca o `authData` no lugar). Daí seguir a chave
      // da sessão em vez de a consultar uma vez — mesma forma que o
      // `SystemModulesStore` usa para os módulos.
      //
      // A chave vem do serviço e não do `AuthStore` de propósito: o serviço já
      // o injeta, enquanto importá-lo aqui acrescentava mais uma aresta ao
      // ciclo `shared-push-notification -> account-data-access` que o
      // `enforce-module-boundaries` já assinala.
      let loadedForSession: string | null = null;

      effect(() => {
        const sessionKey = clientNotificationService.sessionKey();
        const isTemporaryAuth = clientNotificationService.isTemporaryAuthActive();

        untracked(() => {
          // Elevação temporária: o `authData` é o do supervisor que autorizou a
          // ação, por 30s. Recarregar aqui punha as notificações DELE no sino de
          // quem está a operar, e recarregava outra vez ao repor. Mesma guarda
          // que o `SystemModulesStore` faz para os módulos. Ao repor, a chave
          // volta à que já está carregada e nada mais acontece.
          if (isTemporaryAuth) return;

          if (!sessionKey) {
            // Logout. Zerar a marca faz com que voltar a entrar recarregue,
            // mesmo sendo a mesma pessoa; e limpar a lista evita que as
            // notificações de quem saiu fiquem no ecrã para o próximo.
            loadedForSession = null;
            patchState(state, {
              notificationsMenu: {
                ...state.notificationsMenu(),
                notifications: [],
                currentPage: 1,
                hasMoreItems: true,
                // O separador selecionado é um signal LOCAL da sidebar, que
                // nasce em "All"; deixar o `origin` da sessão anterior aqui
                // fazia a lista vir filtrada por um separador que a UI mostra
                // como não escolhido.
                origin: null,
              },
              ...setPropInit('loadNotifications'),
            });
            state.reopenListenConnection();
            return;
          }

          if (sessionKey === loadedForSession) return;

          loadedForSession = sessionKey;
          state.loadNotifications();
          state.reopenListenConnection();
        });
      });
    }
  }),
);
